'use client';
import { IconButton } from '@/components/commonComponents/iconButton/iconButton';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import FlipBook from 'react-pageflip';

import { convertDateStringToDutchDate } from '@/helpers/dateHelper';
import { Heading } from '../../commonComponents/heading/heading';
import { Container } from '../../pageComponents/container/container';
import { TextBlock } from '../textBlock/textBlock';
import styles from './friendBook.module.scss';
import { getAge, getStarsign, themes } from './friendBook.utils';
import { ButtonType } from '@/types/misc';
import { CapiPageV2 } from '@/types/capi';
import { Section } from '@/components/pageComponents/section';
import { genSlug } from '@/helpers/genSlug';

export interface FriendBookProps extends ButtonType {
  title?: string;
  copy?: string;
  currentCapi: string;
  capis: CapiPageV2[];
}

// react-pageflip doesn't include types, this is to bypass the type 'never' error.
type FlipBookType = {
  pageFlip: () => {
    flipNext: () => void;
    flipPrev: () => void;
  };
};

export const FriendBook = ({
  title,
  copy,
  currentCapi,
  capis,
}: FriendBookProps) => {
  const initialIndex = capis.findIndex(
    ({ attributes: c }: CapiPageV2) =>
      genSlug(c.firstName + ' ' + c.lastName) === currentCapi
  );
  const defaultTemplate = 'dinosaur';
  const bookRef = useRef<FlipBookType>(null);
  const [isMobile, setIsMobile] = useState(false);

  const [width, setWidth] = useState(getResponsiveWidth());

  useEffect(() => {
    const handleResize = () => {
      setWidth(getResponsiveWidth());
      if (window.innerWidth < 1280) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
  });

  const onChangeCapi = (index: number) => {
    const newIndex = index >= 0 ? index % capis.length : capis.length - 1;
    const { attributes: c } = capis[newIndex];
    window.history.replaceState(
      null,
      '',
      `/team/${genSlug(c.firstName + ' ' + c.lastName)}`
    );
  };
  return (
    <Section>
      {(title || copy) &&
        (copy ? (
          <TextBlock title={title} copy={copy} />
        ) : (
          <Container size="narrow">
            <Heading level="h2" style="regular" tag="h2">
              {title}
            </Heading>
          </Container>
        ))}
      <div className={styles.flipbookContainer}>
        <div className={styles.cover}>
          <div className={styles.coverLeft} />
          <div
            className={styles.layersLeft}
            style={{ height: isMobile ? 633 : 733 }}
          />
          <div className={styles.coverMiddle} />
          <div
            className={styles.layersRight}
            style={{ height: isMobile ? 633 : 733 }}
          />
          <div className={styles.coverRight} />
          <FlipBook
            // FlipBook doesn't resize when width/height changes, so we need to force a re-render by changing the key
            key={`${width}-${isMobile ? 633 : 733}`}
            ref={bookRef}
            startPage={0}
            showCover={false}
            width={width}
            height={isMobile ? 633 : 733}
            size="stretch"
            mobileScrollSupport
            className={styles.flipbook}
            minWidth={width}
            maxWidth={1000}
            minHeight={isMobile ? 633 : 733}
            maxHeight={1533}
            drawShadow
            flippingTime={750}
            usePortrait={isMobile}
            startZIndex={0}
            autoSize={false}
            maxShadowOpacity={0.5}
            clickEventForward
            useMouseEvents={!isMobile}
            swipeDistance={0}
            showPageCorners
            disableFlipByClick={false}
            style={{}}
            onFlip={(e) => {
              onChangeCapi(Math.floor(e.data / 2));
            }}
            onInit={(e) => {
              const targetPage = initialIndex * 2;
              // `.flip` has a bug and it switches back to page 1 after
              // e.object.flip(targetPage, 'top');
              e.object.turnToPage(targetPage, 'top');
            }}
          >
            {capis.flatMap(
              (
                {
                  attributes: capi,
                  attributes: {
                    friend_book: { data: friend_book_data },
                  },
                },
                index
              ) => {
                const friend_book = friend_book_data?.attributes;
                const themeName = (friend_book?.template ||
                  defaultTemplate) as keyof typeof themes;
                const theme = themes[themeName];
                const themeStyles = styles[`template-${themeName}`];

                return [
                  <div
                    key={`${index}-1`}
                    className={`${styles.page} ${styles.left} ${themeStyles}`}
                  >
                    <div className={styles.row}>
                      <div className={`${styles.imageContainer}`}>
                        <Image
                          fill
                          priority
                          className={`${styles.image} ${styles.capiImage} ${styles.round}`}
                          src={capi.thumbnail.data.attributes.url}
                          alt={capi.firstName}
                          sizes="(max-width: 639px) 100vw,
              (max-width: 1279px) 50vw,
              33vw"
                        />
                      </div>
                      {!isMobile && (
                        <div className={`${styles.imageContainer}`}>
                          <Image
                            fill
                            className={styles.image}
                            src={theme.image1}
                            alt={themeName}
                            priority
                            sizes="(max-width: 639px) 100vw,
              (max-width: 1279px) 50vw,
              33vw"
                          />
                        </div>
                      )}
                    </div>
                    <h3>
                      {capi.firstName} {capi.lastName}
                    </h3>
                    <div className={styles.questions}>
                      <div>
                        🎂 Ik ben jarig op:{' '}
                        {convertDateStringToDutchDate(capi.birthday)}
                      </div>
                      <div>
                        🌌 Mijn sterrenbeeld is:{' '}
                        {getStarsign(new Date(capi.birthday))}
                      </div>
                      <div>🧗‍♀️ Mijn hobbies zijn: {friend_book?.hobbies}</div>
                      <div>
                        👵 Hoe oud ben ik: {getAge(new Date(capi.birthday))}
                      </div>
                      <div>👎 Ik hou niet van: {friend_book?.i_dont_like}</div>
                      <div>👍 Ik hou wel van: {friend_book?.i_do_like}</div>
                      <div>🏡 Waar woon ik: {friend_book?.i_live_in}</div>
                    </div>
                    <div
                      className={`${styles.imageContainer} ${styles.image2}`}
                    >
                      <Image
                        fill
                        className={`${styles.image}`}
                        src={theme.image2}
                        alt={themeName}
                        priority
                        sizes="(max-width: 639px) 100vw,
              (max-width: 1279px) 50vw,
              33vw"
                      />
                    </div>
                  </div>,
                  <div
                    key={`${index}-2`}
                    className={`${styles.page} ${styles.right} ${themeStyles}`}
                    style={{ borderRight: `solid red ${index}px` }}
                  >
                    <h3>[code]capi</h3>
                    <div className={styles.questions}>
                      <div>
                        😄 Het leukste aan [code]capi vind ik:{' '}
                        {friend_book?.the_most_fun_about_codecapi_is}
                      </div>
                      <div>
                        💼 Hoe lang werk ik al bij [code]capi:{' '}
                        {friend_book?.employee_since
                          ? getAge(new Date(friend_book?.employee_since))
                          : ''}
                      </div>
                      <div>
                        🎭 Het leukste teamuitje was:{' '}
                        {friend_book?.the_best_team_outing_was}
                      </div>
                      <div>
                        🧑‍🤝‍🧑 Ik werk veel samen met:{' '}
                        {friend_book?.i_work_together_with}
                      </div>
                      <div>
                        🧒 Wat ik later wil worden:{' '}
                        {friend_book?.what_i_want_to_be_later}
                      </div>
                      <div>
                        ❓ Wat voor Capi ben ik:{' '}
                        {friend_book?.what_kind_of_capi_am_i}
                      </div>
                      <div>
                        🏢 Mijn project is bij: {friend_book?.my_project_is_at}
                      </div>
                      <div>🥹 Ik ben trots op: {friend_book?.i_am_proud_of}</div>
                    </div>
                    <h3>Favorieten</h3>
                    <div className={styles.questions}>
                      <div>Website: {friend_book?.favorite_website}</div>
                      <div>Muziek: {friend_book?.favorite_music}</div>
                      <div>Kleur: {friend_book?.favorite_color}</div>
                      <div>Sport: {friend_book?.favorite_sport}</div>
                      <div>Land: {friend_book?.favorite_country}</div>
                      <div>Eten: {friend_book?.favorite_food}</div>
                      <div>Film: {friend_book?.favorite_film}</div>
                      <div>Boek: {friend_book?.favorite_book}</div>
                      <div>Game: {friend_book?.favorite_game}</div>
                    </div>
                    <div className={`${styles.image3}`}>
                      <Image
                        fill
                        className={`${styles.image}`}
                        src={theme.image3}
                        alt={themeName}
                        priority
                        sizes="(max-width: 639px) 100vw,
              (max-width: 1279px) 50vw,
              33vw"
                      />
                    </div>
                  </div>,
                ];
              }
            )}
          </FlipBook>
        </div>
        <div className={styles.bookNavigation}>
          <IconButton
            style="secondary"
            shape="square"
            icon="ArrowLeft"
            onClick={() => {
              bookRef.current?.pageFlip().flipPrev();
            }}
          />
          <IconButton
            style="secondary"
            shape="square"
            icon="ArrowRight"
            onClick={() => bookRef.current?.pageFlip().flipNext()}
          />
        </div>
      </div>
    </Section>
  );
};

function getResponsiveWidth() {
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 375;

  const [, { width }] = [
    ...new Map<
      number,
      {
        width: number;
      }
    >([
      [375, { width: 355 }],
      [390, { width: 370 }],
      [430, { width: 410 }],
      [500, { width: 450 }],
      [768, { width: 500 }],
      [1200, { width: 550 }],
    ]).entries(),
  ]
    .reverse()
    .find(([query]) => windowWidth >= query) ?? [375, { width: 355 }];

  return width;
}
